import React, { useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import { IconButton, cn } from "../ui";

/**
 * ExamDialog — the one overlay the exam screen uses: the review sheet, the
 * question list on a phone, the shortcuts card.
 *
 * Not AppModal, for three reasons the exam has and other pages do not:
 *   - it must be able to refuse dismissal (an auto-submit in progress has no
 *     "back"), including Escape and a tap outside;
 *   - it is a bottom sheet on a phone and a centred panel from `sm` up;
 *   - while open it takes the keyboard. The runner's shortcuts listen on the
 *     window, so without this an arrow key pressed in the review sheet would
 *     move the paper behind it.
 *
 * Focus moves into the panel on open, Tab stays inside it, and focus returns to
 * whatever opened it.
 */

const SIZES = {
  sm: "sm:tw-max-w-md",
  md: "sm:tw-max-w-xl",
  lg: "sm:tw-max-w-3xl",
};

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function ExamDialog({
  open,
  title,
  description,
  onClose,
  dismissible = true,
  size = "md",
  footer,
  children,
  bodyClassName = "",
}) {
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  // Held in a ref so a parent re-render (a save landing, a status line
  // changing) does not re-run the open effect and yank focus back to the top.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const dismissRef = useRef(dismissible);
  useEffect(() => { dismissRef.current = dismissible; }, [dismissible]);

  useEffect(() => {
    if (!open) return undefined;
    const restore = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    (first || panel)?.focus?.();

    const onKey = (e) => {
      // Capture phase + stopPropagation: the runner's own shortcuts never see a
      // key pressed while this is open.
      e.stopPropagation();
      if (e.key === "Escape") {
        e.preventDefault();
        if (dismissRef.current) onCloseRef.current?.();
        return;
      }
      if (e.key === "Tab" && panel) {
        const items = [...panel.querySelectorAll(FOCUSABLE)];
        if (!items.length) { e.preventDefault(); return; }
        const firstItem = items[0];
        const lastItem = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstItem) {
          e.preventDefault();
          lastItem.focus();
        } else if (!e.shiftKey && document.activeElement === lastItem) {
          e.preventDefault();
          firstItem.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      restore?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-modal tw-flex tw-items-end tw-justify-center tw-bg-overlay sm:tw-items-center sm:tw-p-6 tw-animate-panel-fade"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cn(
          "tw-flex tw-max-h-[92dvh] tw-w-full tw-flex-col tw-overflow-hidden tw-outline-none",
          "tw-rounded-t-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-shadow-popover",
          "sm:tw-rounded-lg tw-animate-slide-up sm:tw-animate-dialog-in",
          SIZES[size] || SIZES.md,
        )}
      >
        <header className="tw-flex tw-items-start tw-gap-3 tw-px-5 tw-pb-3 tw-pt-5 sm:tw-px-6">
          <div className="tw-min-w-0 tw-flex-1">
            <h2 id={titleId} className="tw-m-0 tw-text-lg tw-font-semibold tw-leading-snug tw-text-content">
              {title}
            </h2>
            {description && (
              <p id={descId} className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-content-muted">
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <IconButton icon={X} label="Close" variant="ghost" size="sm" onClick={onClose} />
          )}
        </header>

        <div className={cn("tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-pb-5 sm:tw-px-6", bodyClassName)}>
          {children}
        </div>

        {footer && (
          <footer className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-px-5 tw-py-4 tw-pb-[max(1rem,env(safe-area-inset-bottom))] sm:tw-px-6">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
